export interface EquityRowConfig {
  code?: string;
  labelKey: string;
  indent?: 0 | 1 | 2;
  bold?: boolean;
  italic?: boolean;
}

const OCI_DETAIL_LABEL_KEYS = [
  "equityChanges.rows.ociDebtInstruments",
  "equityChanges.rows.ociEquityInstruments",
  "equityChanges.rows.ociFixedAssets",
  "equityChanges.rows.ociAssociates",
  "equityChanges.rows.ociActuarial",
  "equityChanges.rows.ociTaxRate",
  "equityChanges.rows.ociCashFlowHedge",
  "equityChanges.rows.ociNetInvestmentHedge",
  "equityChanges.rows.ociFx",
];

const OWNER_DETAIL_LABEL_KEYS = [
  "equityChanges.rows.ownerContributions",
  "equityChanges.rows.shareIssue",
  "equityChanges.rows.shareIssueBusinessCombination",
  "equityChanges.rows.convertibleEquityComponent",
  "equityChanges.rows.dividends",
  "equityChanges.rows.otherDistributions",
  "equityChanges.rows.otherOwnerOperations",
  "equityChanges.rows.subsidiaryShareChanges",
];

const SHARE_REWARD_SUB_LABEL_KEYS = [
  "equityChanges.rows.shareRewardsCost",
  "equityChanges.rows.shareRewardsIssue",
  "equityChanges.rows.shareRewardsTax",
];

interface YearBlockConfig {
  policyChangeCode: string;
  recalcCode: string;
  recalcLabelKey: string;
  totalIncomeCode: string;
  totalIncomeLabelKey: string;
  profitCode: string;
  ociTotalCode: string;
  ociTotalLabelKey: string;
  ociFirstDetailCode: number;
  ownerOpsCode: string;
  ownerOpsLabelKey: string;
  shareRewardsCode: string;
  ownerFirstDetailCode: number;
  otherOperationsCode: string;
}

function buildYearBlock(config: YearBlockConfig): EquityRowConfig[] {
  const rows: EquityRowConfig[] = [
    { code: config.policyChangeCode, labelKey: "equityChanges.rows.policyChange" },
    { code: config.recalcCode, labelKey: config.recalcLabelKey, bold: true },
    { code: config.totalIncomeCode, labelKey: config.totalIncomeLabelKey, bold: true },
    { code: config.profitCode, labelKey: "equityChanges.rows.profitYear", indent: 1 },
    { code: config.ociTotalCode, labelKey: config.ociTotalLabelKey, indent: 1 },
    { labelKey: "equityChanges.rows.inTom", indent: 2, italic: true },
  ];
  OCI_DETAIL_LABEL_KEYS.forEach(function pushOciDetail(labelKey, index) {
    rows.push({ code: String(config.ociFirstDetailCode + index), labelKey, indent: 2 });
  });
  rows.push({ code: config.ownerOpsCode, labelKey: config.ownerOpsLabelKey, bold: true });
  rows.push({ labelKey: "equityChanges.rows.inTom", indent: 1, italic: true });
  rows.push({ code: config.shareRewardsCode, labelKey: "equityChanges.rows.shareRewards", indent: 1 });
  rows.push({ labelKey: "equityChanges.rows.inTom", indent: 2, italic: true });
  SHARE_REWARD_SUB_LABEL_KEYS.forEach(function pushShareRewardSub(labelKey) {
    rows.push({ labelKey, indent: 2, italic: true });
  });
  OWNER_DETAIL_LABEL_KEYS.forEach(function pushOwnerDetail(labelKey, index) {
    rows.push({ code: String(config.ownerFirstDetailCode + index), labelKey, indent: 1 });
  });
  rows.push({ code: config.otherOperationsCode, labelKey: "equityChanges.rows.otherOperations" });
  return rows;
}

export const EQUITY_CHANGES_ROWS: EquityRowConfig[] = [
  { code: "010", labelKey: "equityChanges.rows.openingPrev" },
  ...buildYearBlock({
    policyChangeCode: "011",
    recalcCode: "100",
    recalcLabelKey: "equityChanges.rows.recalcPrev",
    totalIncomeCode: "200",
    totalIncomeLabelKey: "equityChanges.rows.totalIncomePrev",
    profitCode: "210",
    ociTotalCode: "220",
    ociTotalLabelKey: "equityChanges.rows.ociTotalPrev",
    ociFirstDetailCode: 221,
    ownerOpsCode: "300",
    ownerOpsLabelKey: "equityChanges.rows.ownerOpsPrev",
    shareRewardsCode: "310",
    ownerFirstDetailCode: 311,
    otherOperationsCode: "319",
  }),
  { code: "400", labelKey: "equityChanges.rows.openingCurrent", bold: true },
  ...buildYearBlock({
    policyChangeCode: "401",
    recalcCode: "500",
    recalcLabelKey: "equityChanges.rows.recalcCurrent",
    totalIncomeCode: "600",
    totalIncomeLabelKey: "equityChanges.rows.totalIncomeCurrent",
    profitCode: "610",
    ociTotalCode: "620",
    ociTotalLabelKey: "equityChanges.rows.ociTotalCurrent",
    ociFirstDetailCode: 621,
    ownerOpsCode: "700",
    ownerOpsLabelKey: "equityChanges.rows.ownerOpsCurrent",
    shareRewardsCode: "710",
    ownerFirstDetailCode: 711,
    otherOperationsCode: "719",
  }),
  { code: "800", labelKey: "equityChanges.rows.closingCurrent", bold: true },
];
